import {
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
  GiftDeliverySummary,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  GiftOrderForAdmin,
  GiftOrderItemType,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';

export type OrderTypeFilter = 'all' | 'coupon' | 'delivery';

export const paymentStatusLabel: Record<GiftOrderPaymentStatus, string> = {
  [GiftOrderPaymentStatus.NotPaid]: '미결제',
  [GiftOrderPaymentStatus.Waiting]: '입금대기',
  [GiftOrderPaymentStatus.Paid]: '결제완료',
  [GiftOrderPaymentStatus.Confirmed]: '확정',
  [GiftOrderPaymentStatus.CancelRequested]: '취소요청',
  [GiftOrderPaymentStatus.Canceled]: '취소됨',
};

export const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getOrderType = (
  order: GiftOrderForAdmin,
): 'coupon' | 'delivery' | 'mixed' => {
  const hasCoupon = order.orderDetails.some((d) => d.type === GiftOrderItemType.Coupon);
  const hasDelivery = order.orderDetails.some(
    (d) => d.type === GiftOrderItemType.Delivery,
  );
  if (hasCoupon && hasDelivery) return 'mixed';
  if (hasDelivery) return 'delivery';
  return 'coupon';
};

export const getCouponStats = (order: GiftOrderForAdmin) => {
  const coupons = order.coupons;
  if (!coupons || coupons.length === 0) return null;

  const disposedCount = coupons.filter(
    (c) => c.redeemStatus === GiftCouponRedeemStatus.Disposed,
  ).length;
  const canceledCount = coupons.filter(
    (c) => c.redeemStatus === GiftCouponRedeemStatus.Canceled,
  ).length;

  const activeCoupons = coupons.filter(
    (c) =>
      c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
      c.redeemStatus !== GiftCouponRedeemStatus.Disposed,
  );

  const additionalCount = activeCoupons.filter((c) => c.isAdditional).length;
  const reissuedCount = activeCoupons.filter((c) => c.isReissued).length;
  const originalCount = activeCoupons.length - additionalCount - reissuedCount;
  const totalCount = coupons.length;
  const activeCount = activeCoupons.length;

  const notSharedCount = activeCoupons.filter(
    (c) => c.shareStatus === GiftCouponShareStatus.NotShared,
  ).length;
  const unusedCount = activeCoupons.filter(
    (c) =>
      c.redeemStatus !== GiftCouponRedeemStatus.Delivered &&
      c.redeemStatus !== GiftCouponRedeemStatus.Refunded,
  ).length;
  const deliveredCount = activeCoupons.filter(
    (c) => c.redeemStatus === GiftCouponRedeemStatus.Delivered,
  ).length;
  // 나중에 받기 — 교환신청은 안했지만 (Available) 수신번호 인증까지 마쳐 저장된 쿠폰
  const pendingLaterCount = activeCoupons.filter(
    (c) =>
      c.redeemStatus === GiftCouponRedeemStatus.Available &&
      !!c.recipient?.phoneNumber,
  ).length;

  return {
    notSharedCount,
    unusedCount,
    totalCount,
    originalCount,
    additionalCount,
    reissuedCount,
    deliveredCount,
    canceledCount,
    disposedCount,
    activeCount,
    pendingLaterCount,
  };
};

export const getIssueStats = (order: GiftOrderForAdmin) => {
  const coupons = order.coupons;
  if (!coupons || coupons.length === 0) return null;

  let issuedCount = 0;
  let requestedCount = 0;
  let completedCount = 0;

  coupons.forEach((c) => {
    if (c.issued) {
      if (c.isRenewed || c.refund?.refundedAt) {
        completedCount++;
      } else if (c.issued.requestedAt) {
        requestedCount++;
      } else {
        issuedCount++;
      }
    }
  });

  const total = issuedCount + requestedCount + completedCount;
  if (total === 0) return null;

  return { issuedCount, requestedCount, completedCount, total };
};

export const getDeliveryStats = (order: GiftOrderForAdmin) => {
  const deliveries = (order.deliveries ?? []) as GiftDeliverySummary[];
  if (deliveries.length === 0) return null;

  const totalCount = deliveries.length;
  const canceledCount = deliveries.filter(
    (d) => d.deliveryStatus === GiftDeliveryStatus.Canceled,
  ).length;
  const activeCount = totalCount - canceledCount;
  const notSharedCount = deliveries.filter(
    (d) => d.shareStatus === GiftDeliveryShareStatus.NotShared,
  ).length;
  const waitingAddressCount = deliveries.filter(
    (d) => d.deliveryStatus === GiftDeliveryStatus.WaitingAddress,
  ).length;
  const preparingCount = deliveries.filter(
    (d) => d.deliveryStatus === GiftDeliveryStatus.Preparing,
  ).length;
  const shippingCount = deliveries.filter(
    (d) => d.deliveryStatus === GiftDeliveryStatus.Shipping,
  ).length;
  const deliveredCount = deliveries.filter(
    (d) => d.deliveryStatus === GiftDeliveryStatus.Delivered,
  ).length;

  return {
    totalCount,
    notSharedCount,
    waitingAddressCount,
    preparingCount,
    shippingCount,
    deliveredCount,
    canceledCount,
    activeCount,
  };
};
